import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Container } from 'react-bootstrap';
import { Alert, Button } from '@storable/canaveral-components';
import CollapsibleButtons from '../CollapsibleButtons';
import UnitFilter from './UnitFilter';
import {
  TRACK_CLICK_FILTER_APPLIED,
  TRACK_CLICK_CATEGORY_APPLIED,
} from '../../../../src/web/utils/dataLayer';
import { ThemeOne, ThemeTwo, ThemeThree } from './themes';
import classes from './UnitsTable.scss';
import { getQueryParams } from '../../../../modules/search';
import withAlert from '../../../hocs/withAlert';
import useEventTrigger from '../../../../modules/react/hooks/useEventTrigger';

const VIEW_ALL = 'view-all';

const themeMap = {
  one: ThemeOne,
  two: ThemeTwo,
  three: ThemeThree,
};

const propTypes = {
  facility: PropTypes.shape({
    settings: PropTypes.shape({
      softwareProvider: PropTypes.string,
      showUnavailableUnits: PropTypes.bool.isRequired,
    }).isRequired,
    unitGroups: PropTypes.arrayOf(PropTypes.object),
    unitSummary: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      availableUnitsCount: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      siteUnitCategoryId: PropTypes.string.isRequired,
    })),
  }),
  theme: PropTypes.oneOf(Object.keys(themeMap)).isRequired,
  companyName: PropTypes.string,
  alert: PropTypes.node,
};

const defaultProps = {
  facility: {
    settings: {},
    unitGroups: [],
    unitSummary: [],
  },
  companyName: undefined,
  alert: undefined,
};

/**
 * The URL deals with the categories as a position (i.e., 1, 2, 3, etc), but we want to deal
 * with categories as their unique identifier. So this function will attempt to convert the
 * incoming route props to a category ID.
 */
const getSelectedCategory = (unit, unitSummary) => {
  const unitIndex = parseInt(unit, 10);

  if (!Number.isNaN(unitIndex) && unitIndex > 0) {
    const summary = unitSummary[unitIndex - 1];
    return summary ? summary.siteUnitCategoryId : null;
  }

  return null;
};

const UnitsTable = ({
  companyName,
  facility,
  theme,
  alert,
  ...props
}) => {
  const [selectedCategory, setUnitCategory] = useState(VIEW_ALL);
  const [rawFilterText, setFilterText] = useState('');
  const [eventName, setEventName] = useState(null);
  const [analyticsEvent, setAnalyticsEvent] = useState(null);

  const { unitGroups, unitSummary } = facility;
  const { addEvent } = useEventTrigger();

  const getClassForTheme = themeToMap => themeMap[themeToMap];

  useEffect(() => {
    const { unit } = getQueryParams();
    const category = getSelectedCategory(unit, unitSummary);
    if (category) { setUnitCategory(category); }
  }, []);


  const handleFilterChange = (text) => {
    setFilterText(text);
    if (text.length >= 2 || text.length === 0) {
      setEventName({ filtersUsed: text.length === 0 ? 'CLEAR' : text });
      setAnalyticsEvent(TRACK_CLICK_FILTER_APPLIED);
    } else {
      setEventName(null);
      setAnalyticsEvent(null);
    }
  };

  const clearSearch = () => {
    setFilterText('');
    handleFilterChange('');
    setUnitCategory(VIEW_ALL);
  };

  const getAnalyticsPayload = (extraParam, listSize) => ({
    facilityId: facility.id,
    eventLocation: `theme${theme.charAt(0).toUpperCase() + theme.slice(1)}`,
    unitsShown: listSize,
    // geoLocation: undefined, // will implement later
    timeStamp: Date.now(),
    ...extraParam,
  });


  const getUnitCategories = () => {
    const { settings: { showUnavailableUnits } } = facility;

    let summary;
    if (!showUnavailableUnits) {
      summary = unitSummary.filter(({ availableUnitsCount }) => availableUnitsCount > 0);
    } else {
      summary = unitSummary;
    }

    return [
      { id: VIEW_ALL, name: 'View All' },
      ...summary,
    ].map(({ siteUnitCategoryId, id, name }) => ({
      id: siteUnitCategoryId || id,
      content: (
        <span>
          {name}
          <span className="d-inline d-sm-inline d-md-none"> Units</span>
        </span>
      ),
    }));
  };

  const handleCategoryChange = (category) => {
    setUnitCategory(category);

    // Find the object in unitSummary that matches the category id
    const selectedUnit = unitSummary.find(unit => unit.siteUnitCategoryId === category);

    // Fetch the name from the selected object, if found
    const unitName = selectedUnit ? selectedUnit.name : null;
    setEventName({ categoryUsed: unitName });
    setAnalyticsEvent(TRACK_CLICK_CATEGORY_APPLIED);
  };

  const filterUnitGroups = (unitGroupsToFilter) => {
    const { settings: { showUnavailableUnits } } = facility;
    const filterText = rawFilterText.toLowerCase();

    return unitGroupsToFilter.filter(({
      price, size, amenities, availableUnitsCount, category, type,
    }) => {
      // Don't display if the settings don't want unavailable unit groups showing up
      if (!showUnavailableUnits && availableUnitsCount === 0) {
        return false;
      }

      // Don't display if unitGroup has no category
      if (!category) {
        return false;
      }

      // Don't display if there's a selected category and we're not in it
      // but do display if "View All" is selected
      if (selectedCategory && ![category, VIEW_ALL].includes(selectedCategory)) {
        return false;
      }

      let amenityNames = [];
      if (amenities) {
        amenityNames = amenities.map(amenity => amenity.name);
      }

      // Finally, we filter on any search text there is
      if (filterText) {
        const allSearchables = [price, size, type, amenityNames].join(' ').toLowerCase();
        return allSearchables.indexOf(filterText) !== -1;
      }

      return true;
    });
  };

  if (!facility || !unitGroups) {
    // todo: Maybe show some loading stuff
    return false;
  }

  const filteredUnits = filterUnitGroups(unitGroups);


  useEffect(() => {
    if (eventName && analyticsEvent) {
      const payload = getAnalyticsPayload(eventName, filteredUnits.length);
      addEvent(analyticsEvent, payload, 'click');
    }
  }, [eventName, analyticsEvent]);

  return (
    <Container className={`table-theme-${theme}`}>
      <div>
        {alert && alert}
      </div>
      <CollapsibleButtons
        options={getUnitCategories()}
        onSelect={handleCategoryChange}
        selectedOption={selectedCategory}
        wrapperClassName={classes.buttonWrapper}
        buttonClassName={classes.categoryButton}
      />
    <UnitFilter onFilterChange={handleFilterChange} filterText={rawFilterText} />
      {!filteredUnits.length && rawFilterText.length ? (
        <Alert context="info" className={classes.empty}>
          <div>
            No results found matching that search.
          </div>
          <Button context="neutral" onClick={clearSearch} className={classes.clearBtn}>Clear Search</Button>
        </Alert>
      ) : React.createElement(
        getClassForTheme(theme),
        {
          ...props,
          facility,
          units: filteredUnits,
        },
      )}
    </Container>
  );
};

UnitsTable.propTypes = propTypes;
UnitsTable.defaultProps = defaultProps;

/*
Fragmentation of a UnitsTable stops here because rendering it requires a bunch
of child components and would make it difficult to follow how data is being
fetched if we drilled down through all of them to pull in more fragments.
*/
UnitsTable.fragments = {
  facility: gql`
    fragment UnitsTableFacility on Facility {
      applicableFeesEnabled
      address {
        address1
        address2
        city
        country
        fullAddress
        latitude
        longitude
        postal
        state
      }
      directPhone
      discountPlans {
        autoApply
        id
        name
        turnedOn
        description
        publicDescription
        startDate
        endDate
      }
      expirationWindow
      hasRentalCenter
      phone
      displayName
      rentalCenterUrl
      reservationFeeRequired
      reservationWindow
      settings {
        callActionThreshold
        disableReservations
        enableMoveIns
        enablePublicDescription
        hidePricingDisplayCall
        reservationConfirmation
        reservationDisclaimer
        showAvailableUnitCount
        showAvailableUnitCountThreshold
        showUnavailableUnits
        softwareProvider
        voyagerOnly
        enableTieredPricing
      }
      timezone
      unitGroups {
        id
        amenities {
          id
          name
        }
        area
        availableUnitsCount
        category
        discountedPrice
        discountPlans {
          id
          autoApply
          endDate
          publicDescription
          promotionType
        }
        moveInUrl
        price
        reservationUrl
        size
        type
        minimumUnitPrice
        minimumUnitDiscountedPrice
        minimumPriceTierId
        tiers {
          id
          name
          tierId
          availableUnitsCount
          description
          discountedPrice
          price
          rank
          availableUnits
        }
      }
      unitSummary {
        id
        availableUnitsCount
        name
        siteUnitCategoryId
      }
      useExpirationWindow
      useReservationWindow
    }
  `,
};

export default withAlert({
  Component: UnitsTable,
  message: 'If you need to reserve a unit, please try again later or give us a call.',
});
